<script setup lang="ts" generic="Key extends string">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import KpiCard from '@uiKit/components/KpiCard.vue'

// A row of clickable count cards that filter the list beneath them — one card
// selected at a time, the selection a v-model of the card's key. Presentational:
// the caller decides what each key means and what the list fetches.
//
// A card whose count is unknown (the counts read failed) takes `value: null` and
// shows a dash rather than a zero — "0 rejected" would be a claim, "—" is an
// honest "don't know" — and stays clickable.
const selected = defineModel<Key>({ required: true })

const props = defineProps<{
  cards: readonly { key: Key; label: string; value: number | null }[]
  loading: boolean
  /** Names the group for screen readers, e.g. "Filter by status". */
  groupLabel: string
}>()

const columnCount = computed(() => props.cards.length)
</script>

<template>
  <Box
    role="group"
    :aria-label="groupLabel"
    class="filter-cards"
    :style="{ '--filter-card-count': columnCount }"
  >
    <KpiCard
      v-for="card in cards"
      :key="card.key"
      :label="card.label"
      :value="card.value ?? '—'"
      :loading="loading"
      clickable
      :selected="selected === card.key"
      @click="selected = card.key"
    />
  </Box>
</template>

<style scoped>
.filter-cards {
  display: grid;
  grid-template-columns: repeat(var(--filter-card-count), minmax(0, 1fr));
  gap: 0.75rem;
}

/* A scrollable row on a phone: tiles abreast would truncate every label, and
   wrapping would push the list itself below the fold. */
@media (max-width: 768px) {
  .filter-cards {
    grid-template-columns: none;
    grid-auto-flow: column;
    grid-auto-columns: minmax(140px, 1fr);
    overflow-x: auto;
    scrollbar-width: none;
    padding-bottom: 2px;
  }

  .filter-cards::-webkit-scrollbar {
    display: none;
  }
}
</style>
